import { giphyDarkestGrey, giphyLightestGrey, giphyRed } from '@giphy/colors'
import dateFormat from 'dateformat'
import { MouseEvent, useContext, useState } from 'react'
import DotsLoader from 'shared/components/loaders/dots'
import MessageContext from 'shared/contexts/message'
import styled from 'styled-components'
import SuperCutSegmentationType from '../../../../creation-tools/types/super-cut-segmentation-type'
import SuperCutStatus from '../../../types/super-cut-status'
import SuperCutUpload from '../../../types/super-cut-upload'
import AlertModal from '../../alert-modal'
import { PillBlock } from '../../blocks'
import DeleteModal from '../modals/delete-modal'
import StatusBox from '../shared/components/status-box'
import { ICapture, IPending } from '../shared/types'
import API from '../shared/utils/api'

const ROW_HEIGHT = 80

const api = new API()

const Delete = styled.div`
    margin-top: 2px;
    visibility: hidden;
    color: ${giphyRed};
    flex-shrink: 0;
    margin-right: 20px;
`

const Capture = styled.a<{ deleteInProgress: boolean }>`
    display: flex;
    height: ${ROW_HEIGHT}px;
    background-color: ${giphyDarkestGrey};
    margin: 5px 0 5px 0;
    align-items: center;

    &:hover ${Delete} {
        visibility: visible;
    }

    ${({ deleteInProgress }) =>
        deleteInProgress &&
        `
        pointer-events: none;
    `}
`

const Thumbnail = styled.div<{ src: string }>`
    height: ${ROW_HEIGHT}px;
    width: ${ROW_HEIGHT}px;
    background-image: url(${(props) => props.src});
    background-size: cover;
    background-repeat: no-repeat;
    flex-shrink: 0;
`

const PaddedPillBlock = styled(PillBlock)`
    margin-right: 5px;
`

const TitleContainer = styled.div`
    width: 100%;
    margin: 0 25px;
    height: ${ROW_HEIGHT}px;
    line-height: ${ROW_HEIGHT}px;
    min-width: 0;
    display: flex;
    align-items: center;
`

const CaptureTitle = styled.div`
    vertical-align: middle;
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 10px;
`

const Detail = styled.div`
    font-size: 15px;
    font-weight: bold;
    display: inline-block;
    height: ${ROW_HEIGHT}px;
    line-height: ${ROW_HEIGHT}px;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    vertical-align: middle;
    flex-shrink: 0;
    margin-right: 30px;
`

const DateWrapper = styled.div`
    width: 100px;
`

const Icon = styled.i`
    font-size: 20px;
    color: ${giphyLightestGrey};

    &[class~='youtube-shorts-icon'] {
        display: inline-block;
        vertical-align: middle;
        background-image: url(/static/img/svg/youtube-shorts-logo.svg);
        background-size: contain;
        height: 20px;
        width: 20px;
        background-repeat: no-repeat;
        background-position: center;
    }
`

type CompletedRowProps = {
    showUsername: boolean
    capture: ICapture
    onCaptureDeleted: (id: string) => void
}
export const CompletedCaptureRow = ({
    showUsername,
    capture: { id, username, date, status, title, source, thumbnail, segments_count, process_type, upload_metadata },
    onCaptureDeleted,
}: CompletedRowProps) => {
    const [deleteInProgress, setDeleteInProgress] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const { sendMessage } = useContext(MessageContext)

    const captureStatus = SuperCutStatus.getNormalizedStatus(status)
    const captureSource = SuperCutUpload.getNormalizedUploadType(source)
    const captureDate = dateFormat(new Date(date), 'mmm dS, yyyy')

    const onCaptureDeleteClicked = (evt: MouseEvent) => {
        evt.preventDefault()
        setShowDeleteModal(true)
    }

    const cancelDelete = () => {
        setShowDeleteModal(false)
    }

    const deleteCapture = async () => {
        try {
            await api.deleteCapture(id)
            onCaptureDeleted(id)
            sendMessage(`Capture \'${title}\' deleted!`)
        } catch (error) {
            sendMessage(`Unable to delete \'${title}\'`, 'error')
        }
    }

    const confirmDelete = async () => {
        setShowDeleteModal(false)
        setDeleteInProgress(true)
        await deleteCapture()
        setDeleteInProgress(false)
    }

    return (
        <>
            {showDeleteModal && (
                <DeleteModal onClickOutside={cancelDelete}>
                    <AlertModal
                        {...{
                            title: `Are you sure you want to delete this capture?`,
                            stickerId: 'jOsoGmmWGSloPU8fMH',
                            primaryButtonColor: giphyRed,
                            primaryButtonLabel: `Delete`,
                            secondaryButtonLabel: `Cancel`,
                            onPrimaryButtonClick: confirmDelete,
                            onSecondaryButtonClick: cancelDelete,
                        }}
                    />
                </DeleteModal>
            )}
            <Capture
                href={`/supercut/detail/${id}`}
                target="_blank"
                rel="noopener noreferrer"
                deleteInProgress={deleteInProgress}
            >
                {deleteInProgress ? (
                    <DotsLoader />
                ) : (
                    <>
                        <Thumbnail src={thumbnail} />
                        <TitleContainer>
                            <CaptureTitle>{title}</CaptureTitle>
                            <PaddedPillBlock isWide>{segments_count >= 999 ? '999+' : segments_count}</PaddedPillBlock>
                            {process_type === SuperCutSegmentationType.Caption && upload_metadata?.auto_caption && (
                                <PaddedPillBlock>CC</PaddedPillBlock>
                            )}
                        </TitleContainer>

                        <Delete onClick={(evt) => onCaptureDeleteClicked(evt)}>
                            <span className="ss-trash" title="Delete This Capture" />
                        </Delete>
                        {showUsername && !!username && <Detail>{`@${username}`}</Detail>}
                        <Detail>
                            <DateWrapper>{captureDate}</DateWrapper>
                        </Detail>
                        <Detail>
                            <Icon
                                className={SuperCutUpload.icon(captureSource)}
                                title={SuperCutUpload.title(captureSource)}
                            />
                        </Detail>
                        <Detail>
                            <StatusBox status={captureStatus} />
                        </Detail>
                    </>
                )}
            </Capture>
        </>
    )
}

type PendingRowProps = {
    capture: IPending
}
export const PendingCaptureRow = ({ capture: { title, process_type, status, source } }: PendingRowProps) => {
    const captureStatus = SuperCutStatus.getNormalizedStatus(status)
    const captureSource = SuperCutUpload.getNormalizedUploadType(source)

    return (
        <Capture deleteInProgress={false}>
            <TitleContainer>
                <CaptureTitle>{title}</CaptureTitle>
            </TitleContainer>
            {process_type === SuperCutSegmentationType.Caption && (
                <Detail>
                    <PaddedPillBlock>CC</PaddedPillBlock>
                </Detail>
            )}
            <Detail>
                <Icon className={SuperCutUpload.icon(captureSource)} title={SuperCutUpload.title(captureSource)} />
            </Detail>
            <Detail>
                <StatusBox status={captureStatus} />
            </Detail>
        </Capture>
    )
}
